import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { showSnackbar } from 'components/ui/snackbar/helper';

import { Button } from 'components/ui/button';
import Modal from 'components/modal';
import { get, post } from 'helpers/api';

interface Contact {
  id: number;
  name?: string;
}

interface ContactList {
  id: number;
  name: string;
}

interface Selection {
  selected: { [id: number]: boolean };
}

interface AddContactToListModalOwnProps {
  contactIds?: number[];
  show: boolean;
  onHide: () => void;
}

interface AddContactToListModalStateProps {
  contacts: Contact[];
  selection: Selection;
}

type AddContactToListModalProps = AddContactToListModalOwnProps & AddContactToListModalStateProps;

export function AddContactToListModal({
  contactIds,
  show,
  selection,
  onHide,
}: AddContactToListModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ContactList[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedList, setSelectedList] = useState<ContactList | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const resetModalState = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedList(null);
    setIsSaving(false);
    setIsInputFocused(false);
  };

  // Debounced search effect
  useEffect(() => {
    if (selectedList) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await get(`/backoffice/contact_lists/search?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(results);
      } catch (error) {
        console.error('Error searching contact lists:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery, selectedList]);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      resetModalState();
    }
  }, [show]);

  const handleSelectList = (list: ContactList) => {
    setSelectedList(list);
    setSearchQuery(list.name);
    setSearchResults([]);
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();

    if (isSaving || !searchQuery.trim()) return;

    const selectedContactIds = contactIds
      ?? Object.keys(selection.selected).map(id => parseInt(id, 10));

    if (selectedContactIds.length === 0) {
      console.error('No contacts selected');
      return;
    }

    setIsSaving(true);

    let listName = '';
    let url = '';
    let payload = {};

    if (selectedList) {
      listName = selectedList.name;
      url = `/backoffice/contact_lists/${selectedList.id}/add_contacts`;
      payload = { contact_ids: selectedContactIds };
    } else {
      listName = searchQuery.trim();
      url = '/backoffice/contact_lists';
      payload = { contact_list: { name: listName }, contact_ids: selectedContactIds };
    }

    try {
      await post(url, payload);

      resetModalState();
      onHide();
      showSnackbar(
        {
          text: `${selectedContactIds.length} contact${selectedContactIds.length > 1 ? 's' : ''} added to ${listName} successfully.`,
          variant: 'success'
        });
    } catch (error) {
      console.error('Error saving contacts to list:', error);
      const nameTaken = !selectedList && (error as { contact_list?: { name?: string[] } })?.contact_list?.name;
      showSnackbar({
        text: nameTaken
          ? `A list named “${listName}” already exists. Choose another name.`
          : 'Error adding contacts to the list. Please try again.',
        variant: 'danger',
      });
      setIsSaving(false);
    }
  };

  const buttonDisabled = isSaving || !searchQuery.trim();

  const getButtonText = () => {
    if (isSaving) return 'Adding...';
    if (selectedList || !searchQuery.trim()) return 'Add to List';
    return 'Create and Add to List';
  };

  return (
    <Modal className="contacts-modal" show={show} onHide={onHide}>
      <Modal.Header closeButton>
        Add Contacts to List
      </Modal.Header>
      <Modal.Body>
        <label>List Title</label>
        <div className="position-relative">
          <input
            className="form-control"
            name="list-title"
            placeholder="Search for a List"
            ref={inputRef}
            type="text"
            value={searchQuery}
            onBlur={() => setIsInputFocused(false)}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSelectedList(null);
            }}
            onFocus={() => setIsInputFocused(true)}
          />
          {isSearching && (
            <div className="position-absolute" style={{ right: '10px', top: '8px' }}>
              <span aria-hidden="true" className="spinner-border spinner-border-sm" role="status" />
            </div>
          )}
          {isInputFocused && searchResults.length > 0 && (
            <div
              className="list-group position-absolute w-100"
              style={{ zIndex: 1000, maxHeight: '200px', overflow: 'auto' }}
            >
              {searchResults.map(list => (
                <button
                  className="list-group-item list-group-item-action"
                  key={list.id}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectList(list);
                  }}
                >
                  {list.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <p className="text-muted mt-2">This will affect any scheduled and active campaigns associated with this list.</p>
      </Modal.Body>
      <Modal.Footer>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Button label="Cancel" variant="outline" onClick={onHide} />
          <Button disabled={buttonDisabled} variant="secondary" onClick={handleSave}>
            {getButtonText()}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

const mapStateToProps = (state: any) => ({
  contacts: state.search.contacts || [],
  selection: state.selection,
});

export default connect(mapStateToProps)(AddContactToListModal);
